<script setup lang="ts">
import type { DateRange } from "reka-ui";

import {
	computed, ref, shallowRef, watch,
} from "vue";

import {
	CalendarDate, toCalendarDate,
} from "@internationalized/date";
import { useQueryClient } from "@tanstack/vue-query";

import { useDateStore } from "@/stores/dates";

const props = defineProps<{
	queryKeys?: Array<Array<string>>;
	size?: "md" | "sm";
}>();

const dateStore = useDateStore();
const queryClient = useQueryClient();

watch(() => [dateStore.dateRange, dateStore.preset], () => {
	if (props.queryKeys) {
		props.queryKeys.forEach((queryKey) => {
			queryClient.invalidateQueries({
				queryKey,
			});
		});
	}
});

const today = new CalendarDate(
	new Date().getFullYear(),
	new Date().getMonth() + 1,
	new Date().getDate(),
);

const opts = computed(() => {
	return dateStore.presetOptions;
});

const showModal = ref(false);
const draftDateRange = shallowRef<DateRange | undefined>(dateStore.dateRange);

watch(showModal, (isOpen) => {
	if (isOpen) {
		draftDateRange.value = dateStore.dateRange;
	}
});

const handleDateRangeChange = (range: DateRange) => {
	if (dateStore.preset !== "custom" || !range.start || !range.end) {
		return;
	}
	dateStore.dateRange = {
		start: toCalendarDate(range.start),
		end: toCalendarDate(range.end),
	};
};

const handleSelect = () => {
	if (dateStore.preset === "custom") {
		showModal.value = true;
	}
};

</script>
<template>
  <div>
    <USelect
      v-model="dateStore.preset"
      icon="ic-round-calendar-month"
      :items="opts"
      :size="props.size"
      class="w-40 rounded-full"
      :ui="{ content: 'z-10' }"
      @blur="handleSelect"
    />
    <UModal
      v-model:open="showModal"
      title="Select Date Range"
      @close="showModal = false"
    >
      <template #body>
        <UCalendar
          v-model="draftDateRange"
          :number-of-months="2"
          range
          :max-value="today"
          @update:valid-model-value="handleDateRangeChange"
        />
      </template>
    </UModal>
  </div>
</template>
